'use client';
import React, { useCallback, useState } from 'react';
import { useParams } from 'next/navigation';
import { faPlus, faUsers } from '@fortawesome/pro-solid-svg-icons';
import { ImpersonateButton } from '../ImpersonateButton';
import { useDashboard } from 'src/context/providers/DashboardProvider';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useSettings } from 'src/components/Settings/hooks';
import {
  useExportCompanyUsersMutation,
  useGetCompanyByIdQuery,
} from 'src/api/redux';
import { PulseButton, Select, Tooltip, Search } from 'src/components';
import { twMerge } from 'tailwind-merge';
// The toolbar filters the user table, so the status vocabulary lives with it.
// Deep imports on purpose: the UserTable barrel would close an import cycle.
import {
  BULK_ROLES_COPY,
  RESULT_COUNT_COPY,
  STATUS_FILTER_OPTIONS,
} from '../UserTable/constants';
import { getLicenseSummary } from '../UserTable/utils';
import type { UserStatusFilter } from '../UserTable/types';

interface DashboardToolbarProps {
  search: string;
  /** Lifecycle filter applied to the user list. */
  statusFilter?: UserStatusFilter;
  onStatusFilterChange?: (value: UserStatusFilter) => void;
  /** Rows matching the current search and status filter. */
  resultCount?: number;
  /**
   * Set only when the account has more users than one request returns, so the
   * count can say it is showing a capped set rather than implying it is whole.
   */
  loadedCount?: number;
  showUserControls?: boolean;
  showCompanyControls?: boolean;
  onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setSelectedUsers?: (users: MedScout.User[]) => void;
  /** Add user + bulk role add — `userManagementCapabilities.canWrite`. */
  canWrite?: boolean;
  /** Export users to CSV — `userManagementCapabilities.canExport`. */
  canExport?: boolean;
  /** Impersonate the company's support user — `userManagementCapabilities.canImpersonate`. */
  canImpersonate?: boolean;
  /**
   * Called instead of opening the add-user dialog when every license is in use
   * (MKT-383). Without it, the dialog opens as before.
   */
  onSeatLimitReached?: () => void;
}

/**
 *  @description    DashboardToolbar component is used to display the toolbar in the admin dashboard.
 *                  User-control actions are gated on the viewer's user-management
 *                  capabilities (MKT-411); Export was previously ungated.
 * @param param0    search, showUserControls, showCompanyControls, onSearch, setShowDialog, capabilities
 * @returns         DashboardToolbar component
 */
const DashboardToolbar = ({
  search,
  statusFilter = 'all',
  onStatusFilterChange,
  resultCount,
  loadedCount,
  showUserControls = false,
  showCompanyControls = false,
  onSearch,
  setSelectedUsers,
  canWrite = false,
  canExport = false,
  canImpersonate = false,
  onSeatLimitReached,
}: DashboardToolbarProps): React.ReactElement => {
  const { companyId } = useParams<{ companyId: string }>();
  const log = useLogging();
  const { setNotification } = useNotification();
  const { selectedUsers } = useSettings();
  const { closeAllButCurrent } = useDrawer();

  const selectedCount = selectedUsers?.length ?? 0;
  const hasSelection = showUserControls && selectedCount > 0;

  const { data: company } = useGetCompanyByIdQuery(
    {
      companyId: companyId,
    },
    {
      skip: !companyId,
    }
  );

  const supportUser = company?.support_user_id?.toString();

  const [exportCompanyUsers] = useExportCompanyUsersMutation();

  const { setShowAddEditUserModal, setShowAddEditCompanyModal } =
    useDashboard();

  const [isExporting, setIsExporting] = useState(false);

  // An invite consumes a license, so at the seat max the form cannot succeed.
  // Explain that instead of opening it (MKT-383).
  const { availableLicenses, hasLicenseLimit } = getLicenseSummary(company);
  const isAtSeatLimit = hasLicenseLimit && availableLicenses <= 0;

  const handleOpenDialog = useCallback(() => {
    showCompanyControls && setShowAddEditCompanyModal(true);
    if (!showUserControls) return;

    if (isAtSeatLimit && onSeatLimitReached) {
      onSeatLimitReached();
      return;
    }

    setShowAddEditUserModal(true);
  }, [
    isAtSeatLimit,
    onSeatLimitReached,
    showCompanyControls,
    showUserControls,
    setShowAddEditCompanyModal,
    setShowAddEditUserModal,
  ]);

  const handleSetStatusFilter = useCallback(
    (_: React.MouseEvent, value: string | number) => {
      onStatusFilterChange?.(String(value) as UserStatusFilter);
    },
    [onStatusFilterChange]
  );

  const handleExport = useCallback(async () => {
    setIsExporting(true);

    try {
      // Export all users - no filtering for export
      const blob = await exportCompanyUsers({ companyId }).unwrap();

      // Create download link for the blob
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users-export-${
        new Date().toISOString().split('T')[0]
      }.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      log.event('Export Users', {
        source: 'DashboardToolbar',
        company: companyId,
      });
      setNotification({
        title: 'Export Users',
        message: 'Users exported successfully',
        type: 'success',
      });
    } catch (err) {
      log.exception('Export Users', {
        tags: {
          source: 'DashboardToolbar',
          company: companyId,
        },
      });

      setNotification({
        title: 'Export Users',
        message: err?.error_message || 'Failed to export users',
        type: 'error',
      });
    } finally {
      setIsExporting(false);
    }
  }, [companyId, log, setNotification, exportCompanyUsers]);

  const handleSearch = useCallback(
    (value: string) => {
      onSearch({ target: { value } } as React.ChangeEvent<HTMLInputElement>);
    },
    [onSearch]
  );

  const handleClearSelectedUsers = useCallback(() => {
    setSelectedUsers?.([]);
  }, [setSelectedUsers]);

  const handleGroupAdd = useCallback(() => {
    closeAllButCurrent('bulk-role-add');
  }, [closeAllButCurrent]);

  return (
    <div
      className="flex items-center justify-between gap-6 bg-white p-4 w-full"
      role="toolbar"
      aria-label="Dashboard toolbar"
      data-testid="dashboard-toolbar"
    >
      <div
        className="flex items-center gap-4 min-w-0 flex-1"
        role="search"
        aria-label="Search and filter controls"
        data-testid="toolbar-search-section"
      >
        {showUserControls && (
          <div className="flex-shrink-0" data-testid="user-filter-container">
            <Select
              value={statusFilter}
              onChange={handleSetStatusFilter}
              className="w-36 rounded-full"
              aria-label="Filter users by status"
              variant="outlined"
              size="sm"
              data-testid="user-status-filter-select"
            >
              {STATUS_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
        )}
        {showUserControls && typeof resultCount === 'number' && (
          <p
            className="flex-shrink-0 whitespace-nowrap text-xs text-neutral-600"
            role="status"
            aria-live="polite"
            data-testid="user-result-count"
          >
            {RESULT_COUNT_COPY.count(resultCount)}
            {typeof loadedCount === 'number' && (
              <Tooltip content={RESULT_COUNT_COPY.truncatedTooltip}>
                <span className="text-neutral-500">
                  {` · ${RESULT_COUNT_COPY.truncated(loadedCount)}`}
                </span>
              </Tooltip>
            )}
          </p>
        )}
        <div className="flex-1 min-w-0 w-96">
          <Search
            size="sm"
            value={search}
            onChange={onSearch}
            onSearch={handleSearch}
            placeholder="Search users..."
            className="w-full rounded-full"
            aria-label="Search users"
            data-testid="search-input"
          />
        </div>
      </div>

      <div
        className="flex items-center gap-6 flex-shrink-0"
        data-testid="toolbar-actions-section"
      >
        <div
          className="flex items-center gap-3"
          role="group"
          aria-label="Toolbar actions"
          data-testid="toolbar-buttons-group"
        >
          {showUserControls && canWrite && (
            <PulseButton
              size="sm"
              variant="outline"
              color="primary"
              onClick={handleOpenDialog}
              startDecorator={faPlus}
              className="rounded-full"
              aria-label="Add new user"
              data-testid="add-user-button"
            >
              Add user
            </PulseButton>
          )}
          {showUserControls && canWrite && (
            <div className="flex items-center gap-1">
              {/* The Tooltip wraps the button rather than its label: a
                    disabled button emits no mouse events, so a tooltip inside
                    one would never open — which is exactly when the
                    explanation is needed. */}
              <Tooltip
                content={
                  hasSelection
                    ? BULK_ROLES_COPY.enabledTooltip(selectedCount)
                    : BULK_ROLES_COPY.disabledTooltip
                }
                delay={200}
                slotProps={{
                  contentClassName: 'max-w-xs whitespace-normal text-left',
                }}
              >
                <PulseButton
                  size="sm"
                  variant="outline"
                  color="neutral"
                  onClick={handleGroupAdd}
                  startDecorator={faUsers}
                  disabled={!hasSelection}
                  className={twMerge(
                    'rounded-full whitespace-nowrap',
                    !hasSelection && 'opacity-50'
                  )}
                  aria-label={
                    hasSelection
                      ? BULK_ROLES_COPY.enabledTooltip(selectedCount)
                      : BULK_ROLES_COPY.disabledTooltip
                  }
                  data-testid="assign-roles-button"
                >
                  {hasSelection
                    ? `${BULK_ROLES_COPY.label} (${selectedCount})`
                    : BULK_ROLES_COPY.label}
                </PulseButton>
              </Tooltip>
              {/* Announced on selection; the count is visible in the label. */}
              <span
                className="sr-only"
                role="status"
                aria-live="polite"
                data-testid="selected-users-info"
              >
                {hasSelection
                  ? `${selectedCount} ${
                      selectedCount === 1 ? 'user' : 'users'
                    } selected`
                  : ''}
              </span>
              {hasSelection && (
                <PulseButton
                  size="sm"
                  variant="plain"
                  color="neutral"
                  onClick={handleClearSelectedUsers}
                  className="whitespace-nowrap text-sm text-neutral-500 hover:text-neutral-700"
                  aria-label="Clear selected users"
                  data-testid="clear-selected-users-button"
                >
                  {BULK_ROLES_COPY.clear}
                </PulseButton>
              )}
            </div>
          )}
          {showUserControls && canExport && (
            <PulseButton
              size="sm"
              loading={isExporting}
              variant="outline"
              color="neutral"
              onClick={handleExport}
              className="rounded-full w-24"
              aria-label={
                isExporting ? 'Exporting users...' : 'Export users to CSV'
              }
              data-testid="export-button"
            >
              Export
            </PulseButton>
          )}
          {showCompanyControls && (
            <PulseButton
              variant="outline"
              color="primary"
              size="sm"
              onClick={handleOpenDialog}
              startDecorator={faPlus}
              className="rounded-full w-32"
              aria-label="Add new company"
              data-testid="add-company-button"
            >
              Add Company
            </PulseButton>
          )}
          {showUserControls && canImpersonate && (
            <Tooltip content="Impersonate Super User">
              <div data-testid="impersonate-button-wrapper">
                <ImpersonateButton userId={supportUser} />
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardToolbar;
